/**
 * US equity market session clock — client side.
 *
 * Purpose: tell the audible session chimes (see components/MarketChimes.tsx) exactly
 * when today's regular session opens and closes, in epoch milliseconds, so the audio
 * can be scheduled precisely.
 *
 * Why this is a client-side calendar rather than a backend call
 * ------------------------------------------------------------
 * The backend already publishes a holiday-aware `market_open` flag on the live-tape
 * payload (strategy/live_tape_payload.py). We deliberately do NOT reuse it here:
 *
 *   1. The chimes live in the app shell and fire on every page. The live-tape endpoint
 *      takes a fresh IBKR snapshot on every call, so polling it from all nine pages
 *      would add real broker/feed load for what is a decorative cue.
 *   2. A boolean "open/closed" cannot tell you the *boundary* — the 5-minute warning
 *      and the ten-second countdown need to know when the close IS, in advance.
 *   3. The SignalR live-tape broadcast is gated OFF outside regular hours, so a client
 *      watching pushes would never observe the close transition at all.
 *
 * The holiday set below is a direct mirror of strategy/market_calendar._us_market_holidays.
 * Keep the two in sync: if the backend calendar changes, change this too.
 *
 * KNOWN LIMITATIONS (both are cosmetic — nothing here drives a signal or a trade):
 *   * Unscheduled closures (weather, national days of mourning — e.g. 2025-01-09) are
 *     not knowable from a rules-based calendar. The chime would fire on such a day.
 *     The backend calendar has the same blind spot.
 *   * Early-close half days ARE modelled here (13:00 ET), which is MORE than the backend
 *     does — see the note in market_calendar.is_regular_trading_hours(). That backend gap
 *     is a separate, trade-adjacent question and is deliberately not touched from here.
 */

export const ET_ZONE = "America/New_York";

export const REGULAR_OPEN_MIN = 9 * 60 + 30; // 09:30 ET
export const REGULAR_CLOSE_MIN = 16 * 60; // 16:00 ET
export const HALF_DAY_CLOSE_MIN = 13 * 60; // 13:00 ET

export type SessionKind = "regular" | "half" | "closed";

export interface SessionBounds {
  /** ET calendar date this describes, "YYYY-MM-DD". */
  date: string;
  kind: SessionKind;
  /** Epoch ms of 09:30:00 ET. null when the market is closed that day. */
  openMs: number | null;
  /** Epoch ms of 16:00:00 ET (13:00 on half days). null when closed. */
  closeMs: number | null;
}

/* ------------------------------------------------------------------ *
 * date helpers (pure, no Date-parsing of strings — DST-safe)
 * ------------------------------------------------------------------ */

const ymd = (y: number, m: number, d: number): string =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Day-of-week for a Y/M/D, 0=Mon .. 6=Sun (matches Python's date.weekday()). */
function weekdayMon0(y: number, m: number, d: number): number {
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

function addDays(y: number, m: number, d: number, n: number): [number, number, number] {
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return [t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()];
}

/** The n-th `weekday` (0=Mon) of a month, 1-indexed n. */
function nthWeekdayOfMonth(y: number, m: number, weekday: number, n: number): [number, number, number] {
  const firstDow = weekdayMon0(y, m, 1);
  const day = 1 + ((weekday - firstDow + 7) % 7) + (n - 1) * 7;
  return [y, m, day];
}

/** The last `weekday` (0=Mon) of a month. */
function lastWeekdayOfMonth(y: number, m: number, weekday: number): [number, number, number] {
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  let d = lastDay;
  while (weekdayMon0(y, m, d) !== weekday) d -= 1;
  return [y, m, d];
}

/** Computus (Anonymous Gregorian algorithm) — mirrors _easter_sunday. */
function easterSunday(year: number): [number, number, number] {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return [year, month, day];
}

/** Fixed-date holiday shifted to the observed weekday (Sat -> Fri, Sun -> Mon). */
function observedFixedHoliday(y: number, m: number, d: number): [number, number, number] {
  const dow = weekdayMon0(y, m, d);
  if (dow === 5) return addDays(y, m, d, -1); // Saturday -> Friday
  if (dow === 6) return addDays(y, m, d, 1); // Sunday -> Monday
  return [y, m, d];
}

const holidayCache = new Map<number, Set<string>>();

/** NYSE full-closure holidays for a year, as "YYYY-MM-DD". Mirrors _us_market_holidays. */
export function usMarketHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;

  const out = new Set<string>();
  const add = ([y, m, d]: [number, number, number]) => out.add(ymd(y, m, d));

  add(observedFixedHoliday(year, 1, 1)); // New Year's Day
  add(nthWeekdayOfMonth(year, 1, 0, 3)); // MLK Jr. Day — 3rd Monday of January
  add(nthWeekdayOfMonth(year, 2, 0, 3)); // Washington's Birthday — 3rd Monday of February
  const [ey, em, ed] = easterSunday(year);
  add(addDays(ey, em, ed, -2)); // Good Friday
  add(lastWeekdayOfMonth(year, 5, 0)); // Memorial Day — last Monday of May
  if (year >= 2022) add(observedFixedHoliday(year, 6, 19)); // Juneteenth
  add(observedFixedHoliday(year, 7, 4)); // Independence Day
  add(nthWeekdayOfMonth(year, 9, 0, 1)); // Labor Day — 1st Monday of September
  add(nthWeekdayOfMonth(year, 11, 3, 4)); // Thanksgiving — 4th Thursday of November
  add(observedFixedHoliday(year, 12, 25)); // Christmas

  holidayCache.set(year, out);
  return out;
}

/** True when Y/M/D is a trading day at all (weekday, not a full-closure holiday). */
export function isMarketSession(y: number, m: number, d: number): boolean {
  if (weekdayMon0(y, m, d) >= 5) return false;
  return !usMarketHolidays(y).has(ymd(y, m, d));
}

/**
 * True when Y/M/D is an NYSE 1:00 PM early close.
 *
 * Three standing half days, each conditional on the date being a trading day at all —
 * which neatly handles the weekend-observance cases (e.g. when 4 July falls on a
 * Saturday, 3 July IS the observed holiday, so isMarketSession() already excludes it):
 *   * the Friday after Thanksgiving
 *   * 3 July, whenever it is a session
 *   * 24 December, whenever it is a session
 */
export function isHalfDay(y: number, m: number, d: number): boolean {
  if (!isMarketSession(y, m, d)) return false;

  if (m === 7 && d === 3) return true;
  if (m === 12 && d === 24) return true;

  const [ty, tm, td] = nthWeekdayOfMonth(y, 11, 3, 4); // Thanksgiving
  const [fy, fm, fd] = addDays(ty, tm, td, 1); // the Friday after
  return y === fy && m === fm && d === fd;
}

/* ------------------------------------------------------------------ *
 * ET wall-clock <-> epoch conversion
 * ------------------------------------------------------------------ */

/**
 * The UTC offset of America/New_York, in ms, at a given instant.
 * Derived by formatting the instant in ET and diffing against the instant itself,
 * so it needs no hard-coded DST rules and stays correct if the rules ever change.
 */
function etOffsetMsAt(atMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_ZONE,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(atMs));

  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? "0");
  let hour = get("hour");
  if (hour === 24) hour = 0; // en-US hour12:false renders midnight as "24"

  const asIfUtc = Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
  return asIfUtc - Math.floor(atMs / 1000) * 1000;
}

/** Epoch ms for a given ET wall-clock time on a given ET calendar date. */
export function etWallClockToEpochMs(
  y: number,
  m: number,
  d: number,
  minutesFromMidnight: number,
): number {
  const wall = Date.UTC(y, m - 1, d, 0, minutesFromMidnight, 0);
  // Two passes: the first offset lookup uses an instant that may be on the wrong side
  // of a DST boundary; re-resolving with the corrected instant settles it. Market hours
  // are never near the 02:00 ET transition, so one refinement is sufficient.
  let ms = wall - etOffsetMsAt(wall);
  ms = wall - etOffsetMsAt(ms);
  return ms;
}

/** The ET calendar date at a given instant, as [y, m, d]. */
export function etDateAt(atMs: number): [number, number, number] {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(atMs));
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? "0");
  return [get("year"), get("month"), get("day")];
}

/* ------------------------------------------------------------------ *
 * public API
 * ------------------------------------------------------------------ */

/** Open/close boundaries for the ET calendar date containing `atMs`. */
export function sessionBoundsFor(atMs: number): SessionBounds {
  const [y, m, d] = etDateAt(atMs);
  const date = ymd(y, m, d);

  if (!isMarketSession(y, m, d)) {
    return { date, kind: "closed", openMs: null, closeMs: null };
  }

  const half = isHalfDay(y, m, d);
  return {
    date,
    kind: half ? "half" : "regular",
    openMs: etWallClockToEpochMs(y, m, d, REGULAR_OPEN_MIN),
    closeMs: etWallClockToEpochMs(y, m, d, half ? HALF_DAY_CLOSE_MIN : REGULAR_CLOSE_MIN),
  };
}

/** True when `atMs` falls inside regular trading hours (holiday- and half-day-aware). */
export function isMarketOpenAt(atMs: number): boolean {
  const b = sessionBoundsFor(atMs);
  if (b.openMs == null || b.closeMs == null) return false;
  return atMs >= b.openMs && atMs < b.closeMs;
}

/** ET clock time at an instant, formatted "HH:MM" for display. */
export function formatEtTime(atMs: number): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: ET_ZONE,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(atMs));
}

/** The next date on or after `atMs` that is a trading session. */
export function nextSessionBoundsFrom(atMs: number): SessionBounds {
  let [y, m, d] = etDateAt(atMs);
  for (let i = 0; i < 10; i += 1) {
    if (isMarketSession(y, m, d)) {
      const half = isHalfDay(y, m, d);
      return {
        date: ymd(y, m, d),
        kind: half ? "half" : "regular",
        openMs: etWallClockToEpochMs(y, m, d, REGULAR_OPEN_MIN),
        closeMs: etWallClockToEpochMs(y, m, d, half ? HALF_DAY_CLOSE_MIN : REGULAR_CLOSE_MIN),
      };
    }
    [y, m, d] = addDays(y, m, d, 1);
  }
  return { date: ymd(y, m, d), kind: "closed", openMs: null, closeMs: null };
}
